import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard } from 'lucide-react';
import { Switch } from '../ui/switch';
import { money } from './DeskForms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-credit-cashout/admin`;

const AccessBadge = ({ s }) => {
  const label = s.access === 'revoked' ? 'Revoked' : s.access === 'granted' ? 'Granted by admin' : s.tier2_status === 'active' ? 'Tier 2 paid' : 'No access';
  const tone = s.access === 'revoked' ? 'text-red-300' : s.access || s.tier2_status === 'active' ? 'text-emerald-300' : 'text-slate-400';
  return <span className={`text-xs ${tone}`} data-testid={`cashout-access-${s.client_id}`}>{label}</span>;
};

const Row = ({ s, onSet }) => (
  <tr className="border-t border-slate-700" data-testid={`cashout-sub-${s.client_id}`}>
    <td className="py-2 text-xs">{s.name || s.email}<p className="text-slate-500">{s.email}</p></td>
    <td className="text-xs">{money(s.available_usd)} of {money(s.approved_usd)}</td>
    <td>{s.is_owner ? <span className="text-xs text-amber-300" data-testid={`cashout-access-${s.client_id}`}>Owner, always on</span> : <AccessBadge s={s} />}</td>
    <td className="space-x-1 whitespace-nowrap text-right">
      {!s.is_owner && s.access !== 'granted' && <button onClick={() => onSet(s.client_id, 'granted')} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs" data-testid={`cashout-grant-${s.client_id}`}>Grant</button>}
      {!s.is_owner && s.access !== 'revoked' && <button onClick={() => onSet(s.client_id, 'revoked')} className="px-2 py-1 bg-red-600 text-white rounded text-xs" data-testid={`cashout-revoke-${s.client_id}`}>Revoke</button>}
      {!s.is_owner && s.access && <button onClick={() => onSet(s.client_id, null)} className="px-2 py-1 bg-slate-600 text-white rounded text-xs" data-testid={`cashout-reset-${s.client_id}`}>Reset</button>}
    </td>
  </tr>
);

export const CreditCashoutAdmin = ({ token }) => {
  const [d, setD] = useState(null);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => {
    axios.get(`${API}/overview`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setD(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const call = async (fn) => {
    try { const r = await fn(); toast.success(r.data.message); } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
    load();
  };
  if (!d) return null;

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-amber-500/30 space-y-4" data-testid="credit-cashout-admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-400" /> Credit Cash-Out (Tier 2 · ${d.tier2_price_usd}/mo)</h2>
          <p className="text-xs text-slate-400 mt-1">Subscribers with Tier 2 (or access you grant) can receive dollars straight from unused credit. Revoke blocks them even if they pay. Your own admin account always has access, with no fees.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          {d.enabled ? 'On' : 'Off'}
          <Switch checked={d.enabled} onCheckedChange={(v) => call(() => axios.put(`${API}/settings`, { enabled: v }, h))} data-testid="credit-cashout-toggle" />
        </label>
      </div>
      {!d.subscribers.length ? <p className="text-xs text-slate-500">No subscribers with an approved credit line yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm text-slate-300">
          <thead><tr className="text-left text-xs text-slate-500"><th>Subscriber</th><th>Unused credit</th><th>Access</th><th /></tr></thead>
          <tbody>{d.subscribers.map(s => <Row key={s.client_id} s={s} onSet={(id, access) => call(() => axios.post(`${API}/clients/${id}/access`, { access }, h))} />)}</tbody>
        </table></div>
      )}
    </div>
  );
};
